import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, ensureAuthTokenReady } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import './SalonProfile.css';

const ADVANCE_AMOUNT = 11;
const MIN_PHOTOS = 2;
const MAX_PHOTOS = 5;

function SalonProfile() {
  const { salonData } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditingServicesFor, setIsEditingServicesFor] = useState(false);
  const [servicesForLoading, setServicesForLoading] = useState(false);
  // GST / billing (profile.gstSettings) — mirrors mobile's GstSettingsScreen field-for-field
  // so both surfaces read/write the same salons/{salonId} data.
  const [isEditingGst, setIsEditingGst] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);
  const [savedGstSettings, setSavedGstSettings] = useState(null);
  const [gstForm, setGstForm] = useState({ enabled: false, percentage: '18' });
  // Settlement payment details (profile.paymentDetails) — where Ulloor Styles pays
  // out the collected ₹11 booking advances. Same shape the mobile app writes.
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  // Last saved details (seeded from the salon doc, updated after each save) —
  // drives the read-only display and the cancel-reset of the form below.
  const [savedPaymentDetails, setSavedPaymentDetails] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    settlementMethod: 'upi',
    upiId: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
  });
  
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Local mirror of salonData.salonPhotos so the grid updates immediately after a save,
  // without waiting on a fresh AuthContext fetch (same pattern as profileData/gstForm/paymentForm).
  const [displayPhotos, setDisplayPhotos] = useState([]);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  // Items are either kept existing photos ({url, fileName, uploadedAt}) or newly
  // picked local files ({file, previewUrl}) awaiting upload on save.
  const [editingPhotos, setEditingPhotos] = useState([]);
  const [photoSaving, setPhotoSaving] = useState(false);
  const photoFileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    shopName: '',
    ownerName: '',
    address: '',
    landmark: '',
    city: '',
    district: '',
    state: '',
    openingTime: '',
    closingTime: '',
    servicesFor: '',
    services: []
  });

  useEffect(() => {
    if (salonData?.profile) {
      // Ensure services is always an array
      const services = Array.isArray(salonData.profile.services) 
        ? salonData.profile.services 
        : [];
        
      setProfileData({
        shopName: salonData.profile.shopName || '',
        ownerName: salonData.profile.ownerName || '',
        address: salonData.profile.address || '',
        landmark: salonData.profile.landmark || '',
        city: salonData.profile.cityName || '',
        district: salonData.profile.district || '',
        state: salonData.profile.state || '',
        openingTime: salonData.profile.openingTime || '',
        closingTime: salonData.profile.closingTime || '',
        servicesFor: salonData.profile.servicesFor || '',
        services: services
      });

      // Seed payment details form; legacy salons may only have profile.upiId
      const pd = salonData.profile.paymentDetails || null;
      setSavedPaymentDetails(pd);
      setPaymentForm({
        settlementMethod: pd?.settlementMethod || 'upi',
        upiId: pd?.upiId || salonData.profile.upiId || '',
        accountHolderName: pd?.accountHolderName || salonData.profile.shopName || '',
        accountNumber: pd?.accountNumber || '',
        ifscCode: pd?.ifscCode || '',
      });

      const gst = salonData.profile.gstSettings || null;
      setSavedGstSettings(gst);
      setGstForm({
        enabled: gst?.enabled === true,
        percentage: gst?.percentage != null ? String(gst.percentage) : '18',
      });

      setDisplayPhotos(Array.isArray(salonData.salonPhotos) ? salonData.salonPhotos : []);
    }
  }, [salonData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const salonRef = doc(db, 'salons', salonData.salonId);
      await updateDoc(salonRef, {
        'profile.shopName': profileData.shopName,
        'profile.ownerName': profileData.ownerName,
        'profile.address': profileData.address,
        'profile.landmark': profileData.landmark,
        'profile.openingTime': profileData.openingTime,
        'profile.closingTime': profileData.closingTime,
        updatedAt: new Date().toISOString()
      });

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original data
    if (salonData?.profile) {
      // Ensure services is always an array
      const services = Array.isArray(salonData.profile.services) 
        ? salonData.profile.services 
        : [];
        
      setProfileData({
        shopName: salonData.profile.shopName || '',
        ownerName: salonData.profile.ownerName || '',
        address: salonData.profile.address || '',
        landmark: salonData.profile.landmark || '',
        city: salonData.profile.cityName || '',
        district: salonData.profile.district || '',
        state: salonData.profile.state || '',
        openingTime: salonData.profile.openingTime || '',
        closingTime: salonData.profile.closingTime || '',
        servicesFor: salonData.profile.servicesFor || '',
        services: services
      });
    }
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const handleSaveServicesFor = async () => {
    try {
      setServicesForLoading(true);
      setMessage({ type: '', text: '' });

      const salonRef = doc(db, 'salons', salonData.salonId);
      await updateDoc(salonRef, {
        'profile.servicesFor': profileData.servicesFor,
        updatedAt: new Date().toISOString()
      });

      setMessage({ type: 'success', text: 'Services For updated successfully!' });
      setIsEditingServicesFor(false);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update. Please try again.' });
    } finally {
      setServicesForLoading(false);
    }
  };

  // Mirrors the mobile app's buildPaymentDetails (src/utils/paymentDetailsUtils.js)
  // so both surfaces accept exactly the same input and write the same shape.
  const validateAndBuildPaymentDetails = () => {
    const { settlementMethod, upiId, accountHolderName, accountNumber, ifscCode } = paymentForm;
    if (!accountHolderName.trim()) {
      return { error: 'Please enter the account holder name.' };
    }
    if (settlementMethod === 'upi') {
      if (!/^[a-zA-Z0-9._-]{2,}@[a-zA-Z]{2,}$/.test(upiId.trim())) {
        return { error: 'Please enter a valid UPI ID (e.g. yourname@okaxis).' };
      }
      return {
        paymentDetails: {
          settlementMethod: 'upi',
          upiId: upiId.trim(),
          accountHolderName: accountHolderName.trim(),
        },
      };
    }
    if (!accountNumber.trim() || accountNumber.trim().length < 6) {
      return { error: 'Please enter a valid bank account number.' };
    }
    if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(ifscCode.trim())) {
      return { error: 'Please enter a valid IFSC code (e.g. SBIN0001234).' };
    }
    return {
      paymentDetails: {
        settlementMethod: 'bank',
        accountHolderName: accountHolderName.trim(),
        accountNumber: accountNumber.trim(),
        ifscCode: ifscCode.trim().toUpperCase(),
      },
    };
  };

  const handleSavePaymentDetails = async () => {
    const result = validateAndBuildPaymentDetails();
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
      return;
    }

    try {
      setPaymentLoading(true);
      const salonRef = doc(db, 'salons', salonData.salonId);
      await updateDoc(salonRef, {
        'profile.paymentDetails': result.paymentDetails,
        updatedAt: new Date().toISOString(),
      });
      setSavedPaymentDetails(result.paymentDetails);
      setIsEditingPayment(false);
      setMessage({ type: 'success', text: 'Payment details saved — Ulloor Styles will use them to settle your advances.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save payment details. Please try again.' });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCancelPaymentDetails = () => {
    const pd = savedPaymentDetails;
    setPaymentForm({
      settlementMethod: pd?.settlementMethod || 'upi',
      upiId: pd?.upiId || salonData?.profile?.upiId || '',
      accountHolderName: pd?.accountHolderName || salonData?.profile?.shopName || '',
      accountNumber: pd?.accountNumber || '',
      ifscCode: pd?.ifscCode || '',
    });
    setIsEditingPayment(false);
  };

  // Mirrors the mobile app's GstSettingsScreen validation (0.1–100).
  const handleSaveGst = async () => {
    const pct = Number(gstForm.percentage);
    if (gstForm.enabled && (!gstForm.percentage || Number.isNaN(pct) || pct <= 0 || pct > 100)) {
      setMessage({ type: 'error', text: 'Enter a GST percentage between 0.1 and 100.' });
      return;
    }

    try {
      setGstLoading(true);
      const payload = {
        enabled: gstForm.enabled,
        percentage: gstForm.enabled ? pct : (Number(savedGstSettings?.percentage) || 0),
      };
      const salonRef = doc(db, 'salons', salonData.salonId);
      await updateDoc(salonRef, {
        'profile.gstSettings': payload,
        updatedAt: new Date().toISOString(),
      });
      setSavedGstSettings(payload);
      setIsEditingGst(false);
      setMessage({ type: 'success', text: 'GST settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save GST settings. Please try again.' });
    } finally {
      setGstLoading(false);
    }
  };

  const handleCancelGst = () => {
    setGstForm({
      enabled: savedGstSettings?.enabled === true,
      percentage: savedGstSettings?.percentage != null ? String(savedGstSettings.percentage) : '18',
    });
    setIsEditingGst(false);
    setMessage({ type: '', text: '' });
  };

  const handleCancelServicesFor = () => {
    // Reset to original value
    if (salonData?.profile) {
      setProfileData(prev => ({
        ...prev,
        servicesFor: salonData.profile.servicesFor || ''
      }));
    }
    setIsEditingServicesFor(false);
  };

  // ----- Salon Photos editor (update photos after approval, mirrors the mobile app) -----
  const revokeEditingPreviews = (photos) => {
    photos.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
  };

  const handleOpenPhotoEditor = () => {
    const initial = displayPhotos.map((photo, index) => {
      const url = photo?.url || photo;
      return {
        id: photo?.fileName || url || `existing_${index}`,
        url,
        fileName: photo?.fileName,
        uploadedAt: photo?.uploadedAt,
      };
    });
    setEditingPhotos(initial);
    setShowPhotoEditor(true);
  };

  const handleClosePhotoEditor = () => {
    if (photoSaving) return;
    revokeEditingPreviews(editingPhotos);
    setEditingPhotos([]);
    setShowPhotoEditor(false);
  };

  const handleAddPhotoFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setEditingPhotos((prev) => {
      const remainingSlots = MAX_PHOTOS - prev.length;
      if (remainingSlots <= 0) return prev;
      const newItems = files.slice(0, remainingSlots).map((file, index) => ({
        id: `new_${Date.now()}_${index}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      return [...prev, ...newItems];
    });
  };

  const handleRemoveEditingPhoto = (id) => {
    setEditingPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleSavePhotos = async () => {
    if (editingPhotos.length < MIN_PHOTOS) {
      setMessage({ type: 'error', text: `Please keep at least ${MIN_PHOTOS} photos.` });
      return;
    }
    if (editingPhotos.length > MAX_PHOTOS) {
      setMessage({ type: 'error', text: `Please keep a maximum of ${MAX_PHOTOS} photos.` });
      return;
    }

    try {
      setPhotoSaving(true);
      await ensureAuthTokenReady();

      // Delete storage objects for any existing photo that was removed in the editor.
      const keptIds = new Set(editingPhotos.filter((p) => p.url).map((p) => p.fileName || p.url));
      const removedPhotos = displayPhotos.filter((photo) => {
        const url = photo?.url || photo;
        const id = photo?.fileName || url;
        return !keptIds.has(id);
      });
      await Promise.all(
        removedPhotos.map(async (photo) => {
          try {
            const url = photo?.url || photo;
            if (url && url.includes('firebase')) {
              await deleteObject(ref(storage, url));
            }
          } catch (err) {
            // Best-effort cleanup — a stale Storage object isn't worth failing the save over.
          }
        })
      );

      // Upload newly picked files; keep already-hosted photos untouched.
      const mergedPhotos = await Promise.all(
        editingPhotos.map(async (photo, index) => {
          if (photo.url) {
            return {
              url: photo.url,
              uploadedAt: photo.uploadedAt || new Date().toISOString(),
              fileName: photo.fileName || '',
            };
          }
          const fileName = `salon_${salonData.salonId}_${Date.now()}_${index}.jpg`;
          const fileRef = ref(storage, `salon_gallery/${fileName}`);
          await uploadBytes(fileRef, photo.file);
          const url = await getDownloadURL(fileRef);
          return { url, uploadedAt: new Date().toISOString(), fileName };
        })
      );

      const salonRef = doc(db, 'salons', salonData.salonId);
      await updateDoc(salonRef, {
        salonPhotos: mergedPhotos,
        updatedAt: new Date().toISOString(),
      });

      revokeEditingPreviews(editingPhotos);
      setDisplayPhotos(mergedPhotos);
      setEditingPhotos([]);
      setShowPhotoEditor(false);
      setMessage({ type: 'success', text: 'Salon photos updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update photos. Please try again.' });
    } finally {
      setPhotoSaving(false);
    }
  };

  return (
    <div className="salon-profile-page">
      <div className="profile-header">
        <h2>Salon Profile</h2>
        {!isEditing ? (
          <button onClick={() => setIsEditing(true)} className="edit-btn">
            ✏️ Edit Profile
          </button>
        ) : (
          <div className="action-buttons">
            <button onClick={handleSave} className="save-btn" disabled={loading}>
              {loading ? 'Saving...' : '✓ Save'}
            </button>
            <button onClick={handleCancel} className="cancel-btn" disabled={loading}>
              ✕ Cancel
            </button>
          </div>
        )}
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="profile-content">
        {/* Basic Information */}
        <div className="profile-section">
          <h3>Basic Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Salon Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="shopName"
                  value={profileData.shopName}
                  onChange={handleInputChange}
                  placeholder="Enter salon name"
                />
              ) : (
                <p className="value">{profileData.shopName || 'N/A'}</p>
              )}
            </div>

            <div className="form-group">
              <label>Owner Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="ownerName"
                  value={profileData.ownerName}
                  onChange={handleInputChange}
                  placeholder="Enter owner name"
                />
              ) : (
                <p className="value">{profileData.ownerName || 'N/A'}</p>
              )}
            </div>

            <div className="form-group full-width">
              <label>Address</label>
              {isEditing ? (
                <textarea
                  name="address"
                  value={profileData.address}
                  onChange={handleInputChange}
                  placeholder="Enter salon address"
                  rows="3"
                />
              ) : (
                <p className="value">{profileData.address || 'N/A'}</p>
              )}
            </div>

            <div className="form-group">
              <label>Landmark</label>
              {isEditing ? (
                <input
                  type="text"
                  name="landmark"
                  value={profileData.landmark}
                  onChange={handleInputChange}
                  placeholder="Enter landmark"
                />
              ) : (
                <p className="value">{profileData.landmark || 'N/A'}</p>
              )}
            </div>

            <div className="form-group">
              <label>City</label>
              <p className="value readonly">{profileData.city || 'N/A'}</p>
              <small className="hint">Can't change the city</small>
            </div>

            <div className="form-group">
              <label>District</label>
              <p className="value readonly">{profileData.district || 'N/A'}</p>
              <small className="hint">Can't change the district</small>
            </div>

            <div className="form-group">
              <label>State</label>
              <p className="value readonly">{profileData.state || 'N/A'}</p>
              <small className="hint">Can't change the state</small>
            </div>

            <div className="form-group">
              <label>Opening Time</label>
              {isEditing ? (
                <input
                  type="time"
                  name="openingTime"
                  value={profileData.openingTime}
                  onChange={handleInputChange}
                />
              ) : (
                <p className="value">{profileData.openingTime || 'N/A'}</p>
              )}
            </div>

            <div className="form-group">
              <label>Closing Time</label>
              {isEditing ? (
                <input
                  type="time"
                  name="closingTime"
                  value={profileData.closingTime}
                  onChange={handleInputChange}
                />
              ) : (
                <p className="value">{profileData.closingTime || 'N/A'}</p>
              )}
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <p className="value readonly">{salonData?.phoneNumber || 'N/A'}</p>
              <small className="hint">Can't change the phone number</small>
            </div>

            <div className="form-group">
              <label>Approval Status</label>
              <span className={`status-badge ${salonData?.approvalStatus}`}>
                {salonData?.approvalStatus || 'pending'}
              </span>
            </div>
          </div>
        </div>

        {/* Services Information */}
        <div className="profile-section">
          <h3>Services Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <label style={{ margin: 0 }}>Services For</label>
                {!isEditingServicesFor ? (
                  <button 
                    onClick={() => setIsEditingServicesFor(true)} 
                    className="edit-services-for-btn"
                    disabled={isEditing}
                  >
                    ✏️ Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={handleSaveServicesFor} 
                      className="save-services-for-btn"
                      disabled={servicesForLoading}
                    >
                      {servicesForLoading ? 'Saving...' : '✓ Save'}
                    </button>
                    <button 
                      onClick={handleCancelServicesFor} 
                      className="cancel-services-for-btn"
                      disabled={servicesForLoading}
                    >
                      ✕ Cancel
                    </button>
                  </div>
                )}
              </div>
              {isEditingServicesFor ? (
                <select
                  value={profileData.servicesFor}
                  onChange={(e) => setProfileData(prev => ({ ...prev, servicesFor: e.target.value }))}
                  className="services-for-select"
                >
                  <option value="Men">Men</option>
                  <option value="Women">Women</option>
                  <option value="Unisex">Unisex</option>
                </select>
              ) : (
                <p className="value">{profileData.servicesFor || 'N/A'}</p>
              )}
            </div>

            <div className="form-group full-width">
              <label>Services &amp; Pricing</label>
              <div className="services-list services-data-list">
                {(() => {
                  const categories = salonData?.profile?.servicesData?.serviceCategories || [];
                  const combos = salonData?.profile?.servicesData?.combos || [];
                  const serviceCount = categories.reduce(
                    (sum, cat) => sum + (cat.services || cat.serviceList || []).length,
                    0
                  );
                  if (!categories.length && !combos.length) {
                    return <p className="value">No services published yet.</p>;
                  }
                  return (
                    <p className="value">
                      {serviceCount} service{serviceCount === 1 ? '' : 's'} across {categories.length}{' '}
                      categor{categories.length === 1 ? 'y' : 'ies'}
                      {combos.length > 0 ? ` · ${combos.length} combo${combos.length === 1 ? '' : 's'}` : ''}
                    </p>
                  );
                })()}
              </div>
              <button
                type="button"
                className="edit-btn"
                onClick={() => navigate('/salon/services')}
              >
                Manage your services →
              </button>
            </div>
          </div>
        </div>

        {/* Advance Pay (₹11 booking advance) — mandatory platform-wide, no toggle */}
        <div className="profile-section">
          <h3>Advance Pay (₹{ADVANCE_AMOUNT})</h3>
          <div className="advance-pay-row">
            <div className="advance-pay-info">
              <p className="value">
                Active for all salons — customers pay a ₹{ADVANCE_AMOUNT} advance online
                (Razorpay) to confirm their booking; the balance is paid at your salon after
                the service.
              </p>
              <small className="hint">
                Advance Payment is mandatory on Ulloor Styles for every salon — it reduces
                no-shows, fake bookings, and brings more committed customers. The
                ₹{ADVANCE_AMOUNT} is non-refundable to the customer on cancellation (free
                reschedules keep it). Ulloor Styles collects the advances and pays them out to
                you — see the Advance Payments page.
              </small>
            </div>
            <div className="advance-pay-actions">
              <button
                type="button"
                className="edit-btn"
                onClick={() => navigate('/salon/advance-payments')}
              >
                View Advance Payments →
              </button>
            </div>
          </div>
        </div>

        {/* Payment Details — settlement destination for collected ₹11 advances */}
        <div className="profile-section">
          <div className="payment-details-header">
            <h3>Payment Details (UPI / Bank)</h3>
            {!isEditingPayment ? (
              <button
                type="button"
                className="edit-services-for-btn"
                onClick={() => setIsEditingPayment(true)}
              >
                ✏️ Edit
              </button>
            ) : (
              <div className="payment-details-actions">
                <button
                  type="button"
                  className="save-services-for-btn"
                  onClick={handleSavePaymentDetails}
                  disabled={paymentLoading}
                >
                  {paymentLoading ? 'Saving...' : '✓ Save'}
                </button>
                <button
                  type="button"
                  className="cancel-services-for-btn"
                  onClick={handleCancelPaymentDetails}
                  disabled={paymentLoading}
                >
                  ✕ Cancel
                </button>
              </div>
            )}
          </div>
          <small className="hint">
            Ulloor Styles collects the ₹{ADVANCE_AMOUNT} booking advances from your customers and
            pays them out to you using these details. Keep them up to date so your payouts are
            never delayed. This stays in sync with the mobile app.
          </small>

          {isEditingPayment ? (
            <div className="form-grid payment-details-form">
              <div className="form-group">
                <label>Settlement Method</label>
                <select
                  value={paymentForm.settlementMethod}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, settlementMethod: e.target.value }))}
                  className="services-for-select"
                >
                  <option value="upi">UPI ID (recommended)</option>
                  <option value="bank">Bank Account</option>
                </select>
              </div>
              <div className="form-group">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  value={paymentForm.accountHolderName}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, accountHolderName: e.target.value }))}
                  placeholder="e.g. your salon or owner name"
                />
              </div>
              {paymentForm.settlementMethod === 'upi' ? (
                <div className="form-group">
                  <label>UPI ID</label>
                  <input
                    type="text"
                    value={paymentForm.upiId}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, upiId: e.target.value }))}
                    placeholder="e.g. yourname@okaxis"
                    autoCapitalize="none"
                  />
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Account Number</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={paymentForm.accountNumber}
                      onChange={(e) => setPaymentForm((prev) => ({ ...prev, accountNumber: e.target.value.replace(/[^0-9]/g, '') }))}
                      placeholder="Enter bank account number"
                    />
                  </div>
                  <div className="form-group">
                    <label>IFSC Code</label>
                    <input
                      type="text"
                      value={paymentForm.ifscCode}
                      onChange={(e) => setPaymentForm((prev) => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                      placeholder="e.g. SBIN0001234"
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="form-grid payment-details-form">
              {savedPaymentDetails ? (
                <>
                  <div className="form-group">
                    <label>Settlement Method</label>
                    <p className="value">
                      {savedPaymentDetails.settlementMethod === 'bank' ? 'Bank Account' : 'UPI ID'}
                    </p>
                  </div>
                  <div className="form-group">
                    <label>Account Holder Name</label>
                    <p className="value">{savedPaymentDetails.accountHolderName || 'N/A'}</p>
                  </div>
                  {savedPaymentDetails.settlementMethod === 'bank' ? (
                    <>
                      <div className="form-group">
                        <label>Account Number</label>
                        <p className="value">{savedPaymentDetails.accountNumber || 'N/A'}</p>
                      </div>
                      <div className="form-group">
                        <label>IFSC Code</label>
                        <p className="value">{savedPaymentDetails.ifscCode || 'N/A'}</p>
                      </div>
                    </>
                  ) : (
                    <div className="form-group">
                      <label>UPI ID</label>
                      <p className="value">{savedPaymentDetails.upiId || 'N/A'}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="form-group full-width">
                  <p className="payment-details-missing">
                    ⚠ No payment details yet — add your UPI ID (or bank account) so Ulloor Styles
                    can settle your collected advances.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* GST / Billing */}
        <div className="profile-section">
          <div className="payment-details-header">
            <h3>GST / Billing</h3>
            {!isEditingGst ? (
              <button
                type="button"
                className="edit-services-for-btn"
                onClick={() => setIsEditingGst(true)}
              >
                ✏️ Edit
              </button>
            ) : (
              <div className="payment-details-actions">
                <button
                  type="button"
                  className="save-services-for-btn"
                  onClick={handleSaveGst}
                  disabled={gstLoading}
                >
                  {gstLoading ? 'Saving...' : '✓ Save'}
                </button>
                <button
                  type="button"
                  className="cancel-services-for-btn"
                  onClick={handleCancelGst}
                  disabled={gstLoading}
                >
                  ✕ Cancel
                </button>
              </div>
            )}
          </div>
          <small className="hint">
            When enabled, customers see GST added on top of the service price in the booking
            summary, so they know the exact amount to pay at your salon. This stays in sync with
            the mobile app.
          </small>

          {isEditingGst ? (
            <div className="form-grid payment-details-form">
              <div className="form-group">
                <label>Enable GST billing</label>
                <label className="gst-toggle-checkbox">
                  <input
                    type="checkbox"
                    checked={gstForm.enabled}
                    onChange={(e) => setGstForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                  />
                  <span>{gstForm.enabled ? 'Enabled' : 'Disabled'}</span>
                </label>
              </div>
              {gstForm.enabled && (
                <div className="form-group">
                  <label>GST Percentage</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={gstForm.percentage}
                    onChange={(e) => setGstForm((prev) => ({ ...prev, percentage: e.target.value.replace(/[^0-9.]/g, '') }))}
                    placeholder="e.g. 5"
                    maxLength={5}
                  />
                  <small className="hint">Typical GST rate for salon services in India is 5%.</small>
                </div>
              )}
            </div>
          ) : (
            <div className="form-grid payment-details-form">
              <div className="form-group">
                <label>Status</label>
                <p className="value">{savedGstSettings?.enabled ? 'Enabled' : 'Disabled'}</p>
              </div>
              {savedGstSettings?.enabled && (
                <div className="form-group">
                  <label>GST Percentage</label>
                  <p className="value">{savedGstSettings.percentage}%</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Salon Photos */}
        <div className="profile-section">
          <div className="payment-details-header">
            <h3>Salon Photos</h3>
            <button type="button" className="edit-services-for-btn" onClick={handleOpenPhotoEditor}>
              ✏️ Update Photos
            </button>
          </div>
          <div className="photos-section">
            {displayPhotos && displayPhotos.length > 0 ? (
              <div className="photos-grid">
                {displayPhotos.map((photoObj, index) => {
                  const imageUrl = photoObj.url || photoObj;
                  return (
                    <div
                      key={index}
                      className="photo-card"
                      onClick={() => setLightboxIndex(index)}
                    >
                      <img
                        src={imageUrl}
                        alt={`Salon ${index + 1}`}
                        className="photo-thumb"
                        loading="lazy"
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="value">No photos uploaded yet</p>
            )}
          </div>
        </div>

        {lightboxIndex !== null && displayPhotos?.[lightboxIndex] && (
          <div className="photo-lightbox-overlay" onClick={() => setLightboxIndex(null)}>
            <button
              className="photo-lightbox-close"
              onClick={() => setLightboxIndex(null)}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={displayPhotos[lightboxIndex].url || displayPhotos[lightboxIndex]}
              alt={`Salon ${lightboxIndex + 1}`}
              className="photo-lightbox-img"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {showPhotoEditor && (
          <div className="modal-overlay" onClick={handleClosePhotoEditor}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Update Salon Photos</h3>
                <button className="modal-close" onClick={handleClosePhotoEditor} disabled={photoSaving}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p className="ssalon-modal-hint">
                  Keep {MIN_PHOTOS}–{MAX_PHOTOS} photos. Remove a photo with the × on it, or add more
                  below.
                </p>

                <div className="photo-editor-grid">
                  {editingPhotos.map((photo) => (
                    <div key={photo.id} className="photo-editor-item">
                      <img src={photo.url || photo.previewUrl} alt="Salon" className="photo-editor-thumb" />
                      <button
                        type="button"
                        className="photo-editor-remove"
                        onClick={() => handleRemoveEditingPhoto(photo.id)}
                        disabled={photoSaving}
                        aria-label="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {editingPhotos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      className="photo-editor-add-tile"
                      onClick={() => photoFileInputRef.current?.click()}
                      disabled={photoSaving}
                    >
                      <span className="photo-editor-add-icon">+</span>
                      <span>Add Photo</span>
                    </button>
                  )}
                </div>

                <input
                  ref={photoFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAddPhotoFiles}
                  style={{ display: 'none' }}
                />

                {editingPhotos.length < MIN_PHOTOS && (
                  <p className="photo-editor-warning">
                    ⚠ Please keep at least {MIN_PHOTOS} photos.
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={handleClosePhotoEditor} disabled={photoSaving}>
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSavePhotos}
                  disabled={photoSaving || editingPhotos.length < MIN_PHOTOS || editingPhotos.length > MAX_PHOTOS}
                >
                  {photoSaving ? 'Saving...' : 'Update Photos'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div className="profile-section">
          <h3>Additional Information</h3>
          <div className="info-list">
            <div className="info-row">
              <span className="info-label">Profile Completed:</span>
              <span className="info-value">
                {salonData?.profileCompleted ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Account Status:</span>
              <span className="info-value">
                {salonData?.isBlocked ? '🚫 Blocked' : '✓ Active'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Member Since:</span>
              <span className="info-value">
                {salonData?.createdAt 
                  ? salonData.createdAt.toDate 
                    ? salonData.createdAt.toDate().toLocaleDateString() 
                    : new Date(salonData.createdAt).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalonProfile;

